import React, { useState, useEffect } from "react";
import { DatePicker, Button, Popover, Badge } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { get_user_auth } from "../../Controllers/Layouts/Layouts";
import toast from "react-hot-toast";

const { RangePicker } = DatePicker;

const QuickDateRangePicker = (props) => {
  const {
    onChange,
    value,
    placeholder = ["Start Date", "End Date"],
    format = "DD/MM/YYYY",
    className = "",
    size = "middle",
    disabled = false,
    allowClear = true,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [customRange, setCustomRange] = useState(null);
  const [branch, setBranch] = useState();

  const dateFormatList = [format, "DD-MM-YYYY"];

  const fetchData = async () => {
    try {
      const branches = await get_user_auth({});
      console.log("auoijaskafafa", branches);
      if (branches?.status) {
        let data = [];
        setBranch(branches.data.branch.fiscal_years || []);
      } else {
        toast?.error(branches?.message);
      }
    } catch (errors) {
      toast?.error(errors?.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const quickRanges = [
    {
      key: "today",
      label: "Today",
      getRange: () => {
        const today = dayjs();
        return [today.startOf("day"), today.endOf("day")];
      },
    },
    {
      key: "yesterday",
      label: "Yesterday",
      getRange: () => {
        const yesterday = dayjs().subtract(1, "day");
        return [yesterday.startOf("day"), yesterday.endOf("day")];
      },
    },
    {
      key: "thisWeek",
      label: "This Week",
      getRange: () => {
        const start = dayjs().startOf("week");
        const end = dayjs().endOf("week");
        return [start, end];
      },
    },
    {
      key: "lastWeek",
      label: "Last Week",
      getRange: () => {
        const start = dayjs().subtract(1, "week").startOf("week");
        const end = dayjs().subtract(1, "week").endOf("week");
        return [start, end];
      },
    },
    {
      key: "thisMonth",
      label: "This Month",
      getRange: () => {
        const start = dayjs().startOf("month");
        const end = dayjs().endOf("month");
        return [start, end];
      },
    },
    {
      key: "lastMonth",
      label: "Last Month",
      getRange: () => {
        const start = dayjs().subtract(1, "month").startOf("month");
        const end = dayjs().subtract(1, "month").endOf("month");
        return [start, end];
      },
    },
    {
      key: "thisQuarter",
      label: "This Quarter",
      getRange: () => {
        const start = dayjs().startOf("quarter");
        const end = dayjs().endOf("quarter");
        return [start, end];
      },
    },
    {
      key: "lastQuarter",
      label: "Last Quarter",
      getRange: () => {
        const start = dayjs().subtract(1, "quarter").startOf("quarter");
        const end = dayjs().subtract(1, "quarter").endOf("quarter");
        return [start, end];
      },
    },
    {
      key: "thisYear",
      label: "This Year",
      getRange: () => {
        const start = dayjs().startOf("year");
        const end = dayjs().endOf("year");
        return [start, end];
      },
    },
    {
      key: "lastYear",
      label: "Last Year",
      getRange: () => {
        const start = dayjs().subtract(1, "year").startOf("year");
        const end = dayjs().subtract(1, "year").endOf("year");
        return [start, end];
      },
    },
    {
      key: "last7Days",
      label: "Last 7 Days",
      getRange: () => {
        const start = dayjs().subtract(7, "day").startOf("day");
        const end = dayjs().endOf("day");
        return [start, end];
      },
    },
    {
      key: "last30Days",
      label: "Last 30 Days",
      getRange: () => {
        const start = dayjs().subtract(30, "day").startOf("day");
        const end = dayjs().endOf("day");
        return [start, end];
      },
    },
    {
      key: "last90Days",
      label: "Last 90 Days",
      getRange: () => {
        const start = dayjs().subtract(90, "day").startOf("day");
        const end = dayjs().endOf("day");
        return [start, end];
      },
    },
  ];

  const fiscalYearRanges = (branch || []).map((fy, index) => ({
    key: `fy-${fy._id}`,
    label: `FY ${dayjs(fy.start).format("DD MMM YYYY")} - ${dayjs(fy.end).format("DD MMM YYYY")}`,
    getRange: () => [
      dayjs(fy.start).startOf("day"),
      dayjs(fy.end).endOf("day"),
    ],
  }));

  const allRanges = [...quickRanges, ...fiscalYearRanges];
  const handleQuickRangeSelect = (rangeKey) => {
    const range = allRanges.find((r) => r.key === rangeKey);
    if (range) {
      const [start, end] = range.getRange();
      setSelectedRange({
        key: rangeKey,
        label: range.label,
        value: [start, end],
      });
      setCustomRange([start, end]);

      const formattedStart = start.toISOString();
      const formattedEnd = end.toISOString();

      if (onChange) {
        onChange({
          start: formattedStart,
          end: formattedEnd,
          label: range.label,
          key: rangeKey,
        });
      }

      setIsOpen(false);
    }
  };

  const handleCustomRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const start = dayjs(dates[0]);
      const end = dayjs(dates[1]);

      setSelectedRange(null);
      setCustomRange([start, end]);

      const formattedStart = start.toISOString();
      const formattedEnd = end.toISOString();

      if (onChange) {
        onChange({
          start: formattedStart,
          end: formattedEnd,
          label: "Custom Range",
          key: "custom",
        });
      }
    } else {
      setCustomRange(null);
      if (onChange) {
        onChange(null);
      }
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedRange(null);
    setCustomRange(null);
    if (onChange) {
      onChange(null);
    }
  };

  const getDisplayText = () => {
    if (selectedRange) {
      return `${selectedRange.label}`;
    }
    if (customRange && customRange[0] && customRange[1]) {
      return `${customRange[0].format(format)} - ${customRange[1].format(format)}`;
    }
    return "";
  };

  const popoverContent = (
    <div className="p-4 min-w-[320px] max-w-[400px]">
      <div className="text-sm font-medium mb-3 text-gray-700">Quick Select</div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("today")}
          type={selectedRange?.key === "today" ? "primary" : "default"}
        >
          Today
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("yesterday")}
          type={selectedRange?.key === "yesterday" ? "primary" : "default"}
        >
          Yesterday
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("thisWeek")}
          type={selectedRange?.key === "thisWeek" ? "primary" : "default"}
        >
          This Week
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("lastWeek")}
          type={selectedRange?.key === "lastWeek" ? "primary" : "default"}
        >
          Last Week
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("thisMonth")}
          type={selectedRange?.key === "thisMonth" ? "primary" : "default"}
        >
          This Month
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("lastMonth")}
          type={selectedRange?.key === "lastMonth" ? "primary" : "default"}
        >
          Last Month
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("thisQuarter")}
          type={selectedRange?.key === "thisQuarter" ? "primary" : "default"}
        >
          This Quarter
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("lastQuarter")}
          type={selectedRange?.key === "lastQuarter" ? "primary" : "default"}
        >
          Last Quarter
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("thisYear")}
          type={selectedRange?.key === "thisYear" ? "primary" : "default"}
        >
          This Year
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("lastYear")}
          type={selectedRange?.key === "lastYear" ? "primary" : "default"}
        >
          Last Year
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("last7Days")}
          type={selectedRange?.key === "last7Days" ? "primary" : "default"}
        >
          Last 7 Days
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("last30Days")}
          type={selectedRange?.key === "last30Days" ? "primary" : "default"}
        >
          Last 30 Days
        </Button>
        <Button
          size="small"
          onClick={() => handleQuickRangeSelect("last90Days")}
          type={selectedRange?.key === "last90Days" ? "primary" : "default"}
          className="col-span-2"
        >
          Last 90 Days
        </Button>
      </div>

      {fiscalYearRanges.length > 0 && (
        <>
          <div className="text-sm font-medium mb-2">Fiscal Years</div>

          {fiscalYearRanges.map((range) => (
            <Button
              key={range.key}
              size="small"
              className="w-full mb-2"
              onClick={() => handleQuickRangeSelect(range.key)}
              type={selectedRange?.key === range.key ? "primary" : "default"}
            >
              {range.label}
            </Button>
          ))}
        </>
      )}

      <div className="pt-4 mt-2">
        <div className="text-xs text-gray-500 mb-2">Custom Range</div>
        <RangePicker
          format={dateFormatList}
          value={customRange}
          onChange={handleCustomRangeChange}
          className="w-full"
          size="small"
          placeholder={["Start", "End"]}
        />
        {customRange && (
          <div className="mt-2 text-xs text-gray-500">
            Selected: {customRange[0]?.format(format)} -{" "}
            {customRange[1]?.format(format)}
          </div>
        )}
      </div>
      <div className="flex justify-end mt-3 pt-2 ">
        <Button size="small" onClick={() => setIsOpen(false)}>
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <div className={`quick-date-range-picker ${className}`}>
      <Popover
        content={popoverContent}
        trigger="click"
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottomLeft"
        overlayClassName="quick-date-range-popover"
        destroyTooltipOnHide
      >
        <div className="relative inline-block w-full">
          <div
            className={`
              ant-picker ant-picker-borderless w-full cursor-pointer
              ${disabled ? "ant-picker-disabled" : ""}
              ${size === "small" ? "ant-picker-small" : ""}
              ${size === "large" ? "ant-picker-large" : ""}
              border rounded-md px-3 py-1.5 bg-white hover:border-blue-400 transition-colors
              flex items-center justify-between
            `}
            onClick={() => !disabled && setIsOpen(true)}
            style={{
              minHeight:
                size === "small" ? "27px" : size === "large" ? "35px" : "31px",
            }}
          >
            <span
              className={`${!getDisplayText() ? "text-gray-400" : "text-gray-700"} text-[13px] font-normal`}
            >
              {getDisplayText() || placeholder.join(" - ")}
            </span>
            <div className="flex items-center gap-1">
              {allowClear && getDisplayText() && (
                <span
                  className="text-gray-400 hover:text-gray-600 cursor-pointer text-xs px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  ✕
                </span>
              )}
              <Badge
                dot={selectedRange !== null || customRange !== null}
                offset={[5, 0]}
              >
                <CalendarOutlined className="text-gray-400" />
              </Badge>
            </div>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default QuickDateRangePicker;
